// works with drop down menu, but mobile only
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Sparkles, Home, Settings,
  HelpCircle, LogOut, Menu, X,
  ChevronRight, Trophy, Crown, Zap, DatabaseZap,
  TrendingUp, MessageCircle // TrendingUp for Market, MessageCircle for Chat
} from 'lucide-react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useNavigation } from '../../components/context/NavigationContext';
import { createTranslator } from '../../utils/detectLanguage';
import { mobileNavigationPtBR } from '../mappings/mobileNavigation.pt-br';
import { mobileNavigationES } from '../mappings/mobileNavigation.es';


const NavigationButton = ({ icon: Icon, label, onClick, highlight, isActive, glowColor }) => (
  <button
    onClick={onClick}
    className={`w-full flex items-center justify-between px-3 py-2.5
      bg-white/[0.02] border border-white/[0.03]
      ${highlight ? 'border-violet-500/20' : ''}
      ${isActive ? 'bg-white/[0.05] border-white/[0.08]' : 'hover:bg-white/[0.03] hover:border-white/[0.05]'}
      transition-all duration-200 rounded-lg group relative overflow-hidden`}
  >
    <div className="flex items-center gap-3 z-10">
      <Icon className={`w-4 h-4 transition-all duration-200
        ${highlight ? 'text-violet-400/70' : 'text-white/50'}
        ${isActive ? 'text-emerald-400/60' : 'group-hover:text-white/70'}`} />
      <span className={`text-[13px] font-mono tracking-wide transition-all duration-200
        ${highlight ? 'text-violet-400/90' : 'text-white/80'}
        ${isActive ? 'text-white/90' : 'group-hover:text-white/90'}`}>
        {label}
      </span>
    </div>
    <ChevronRight className="w-3 h-3 text-white/20 group-hover:text-white/40 transition-all duration-200" />
  </button>
);

const StatDisplay = ({ userStats, isPro, tLocal }) => (
  <div className="space-y-2 mt-3">
    {/* Points and Rank Display - Terminal Style */}
    <div className="px-3 py-2 bg-white/[0.02] border border-white/[0.03] rounded-lg
      hover:bg-white/[0.03] hover:border-white/[0.05] transition-all duration-200">
      <div className="flex items-center gap-3">
        <div className="p-1.5 rounded bg-emerald-500/[0.08] border border-emerald-500/20">
          <Trophy className="w-3.5 h-3.5 text-emerald-400/60" />
        </div>
        <div className="flex-1">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-mono text-white/80">
              {userStats?.points?.total || 0} {tLocal("points")}
            </span>
            <span className="text-[10px] font-mono text-emerald-400/90">
              +{userStats?.points?.daily || 0} {tLocal("today")}
            </span>
          </div>
          <div className="relative w-full bg-white/[0.05] h-1 rounded-full mt-1.5 overflow-hidden">
            <div
              className="absolute h-full rounded-full bg-gradient-to-r from-emerald-400/40 to-emerald-400/60"
              style={{ width: `${userStats?.rank?.progress || 0}%` }}
            />
          </div>
          <div className="flex items-center text-[10px] font-mono text-emerald-400/90 mt-1">
            <Crown className="w-3 h-3 mr-1" />
            <span>{userStats?.rank?.current || tLocal('Novice Oracle')}</span>
          </div>
        </div>
      </div>
    </div>

    {/* Streak Display - Terminal Style */}
    {userStats?.points?.streak > 0 && (
      <div className="px-3 py-2 bg-white/[0.02] border border-white/[0.03] rounded-lg">
        <div className="flex items-center gap-2">
          <div className="p-1 rounded bg-violet-500/[0.08] border border-violet-500/20">
            <Zap className="w-3 h-3 text-violet-400/60" />
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-mono text-white/70 uppercase tracking-wider">{tLocal("Daily Streak")}</span>
              <span className="text-[11px] font-mono text-violet-400/90">
                {userStats.points.streak} {tLocal("days")}
              </span>
            </div>
          </div>
        </div>
      </div>
    )}
  </div>
);

const MobileNavigation = ({ 
  user,
  showMobileMenu,
  setShowMobileMenu,
  // showBackToChat,
  openLogoutModal,
  inChatWindow = false // New prop to detect if we're in ChatWindow

}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { inChatInterface } = useNavigation(); // Use the new context
  const [menuButtonHovered, setMenuButtonHovered] = useState(false);
  const [userStats, setUserStats] = useState(null);
  const [isPro, setIsPro] = useState(false);
  // Helper function to get translated text
  const tLocal = createTranslator({
    'pt-br': mobileNavigationPtBR,
    'es': mobileNavigationES
  });


  useEffect(() => {
    const fetchUserData = async () => {
      if (user?.uid) {
        // Fetch user stats
        const userStatsRef = doc(db, "userStats", user.uid);
        const userStatsDoc = await getDoc(userStatsRef);
        if (userStatsDoc.exists()) {
          setUserStats(userStatsDoc.data());
        }

        // Fetch user tier data
        const userRef = doc(db, "users", user.uid);
        const userDoc = await getDoc(userRef);
        if (userDoc.exists()) {
          setIsPro(userDoc.data().tier === 'pro');
        }
      }
    };
    fetchUserData();
  }, [user?.uid]);

  const handleNavigation = (path) => {
    navigate(path);
    setTimeout(() => setShowMobileMenu(false), 150);
  };
  
  // // Style based on location
  // const buttonPosition = inChatWindow 
  //   ? "relative p-2.5 rounded-lg bg-transparent hover:bg-slate-700/30 text-white transition-all duration-200 active:scale-95" 
  //   : "fixed top-4 left-4 z-50 p-2.5 rounded-xl bg-slate-800/90 backdrop-blur-md shadow-lg border border-white/10 hover:border-white/20 hover:bg-slate-700/90 active:scale-95 transition-all duration-200 group";


  return (
    <div className="md:hidden">
      {/* Menu Trigger Button */}
      {/* Only show the button if not in chat window */}

      {!inChatInterface && (
        <button
          onMouseEnter={() => setMenuButtonHovered(true)}
          onMouseLeave={() => setMenuButtonHovered(false)}
          onClick={() => setShowMobileMenu(true)}
          className="fixed top-4 left-4 z-50 p-3 rounded-lg
            bg-black/90 backdrop-blur-sm
            border border-emerald-500/20 hover:border-emerald-500/30
            hover:bg-black/80 active:scale-95
            transition-all duration-200 group"
        >
          <Menu className={`w-5 h-5 text-white/70 hover:text-white/90 transition-all duration-200
            ${menuButtonHovered ? 'scale-110' : ''}`} />
        </button>
      )}

      {showMobileMenu && (
        <>
          {/* Backdrop */}
          <div
            onClick={() => setShowMobileMenu(false)}
            className="fixed inset-0 z-[125] bg-black/60 backdrop-blur-sm animate-in fade-in duration-200"
          />

          {/* Menu Panel - Terminal Glassmorphism */}
          <div className="fixed inset-y-0 left-0 z-[130] w-[85%] max-w-[360px]
            bg-black/95 backdrop-blur-lg flex flex-col animate-in slide-in-from-left duration-300
            border-r border-white/[0.05]">
            
            {/* Header - Terminal Glassmorphism */}
            <div className="p-4 border-b border-white/[0.05] bg-black/50">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="relative group">
                    <div className="w-10 h-10 rounded-lg bg-white/[0.02] border border-white/[0.05]
                      flex items-center justify-center
                      group-hover:bg-white/[0.03] group-hover:border-white/[0.08]
                      transition-all duration-200">
                      <span className="text-sm font-mono text-white/80">
                        {user?.displayName?.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    {isPro && (
                      <div className="absolute -top-1 -right-1 bg-gradient-to-r from-amber-400 to-amber-500 
                        rounded-full p-1 shadow-lg group-hover:scale-110 transition-transform">
                        <Crown className="w-3 h-3 text-slate-900" />
                      </div>
                    )}
                  </div>
                  <div>
                    <div className="text-[13px] font-mono text-white/90 flex items-center gap-2">
                      {user?.displayName}
                      {isPro ? (
                        <span className="text-[9px] font-mono px-1.5 py-0.5
                          bg-violet-500/[0.08] text-violet-400/90
                          border border-violet-500/20 rounded">
                          {tLocal("PRO")}
                        </span>
                      ) : (
                        <span
                          className="text-[9px] font-mono px-1.5 py-0.5
                            bg-white/[0.02] text-white/70
                            border border-white/[0.05] rounded hover:text-white/90
                            hover:border-white/[0.08] transition-all cursor-pointer"
                          onClick={() => handleNavigation('/upgrade')}
                        >
                          {tLocal("Upgrade to Pro?")}
                        </span>
                      )}
                    </div>
                    <div className="text-[10px] font-mono text-white/70">
                      {user?.email}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setShowMobileMenu(false)}
                  className="p-2 -mr-1 rounded-lg bg-white/[0.02]
                    border border-white/[0.03] text-white/50
                    hover:bg-white/[0.05] hover:border-white/[0.08] hover:text-white/70
                    transition-all duration-200 group"
                >
                  <X className="w-4 h-4 group-hover:rotate-90 transition-transform duration-200" />
                </button>
              </div>

              {/* User Stats */}
              {userStats && <StatDisplay userStats={userStats} isPro={isPro} tLocal={tLocal} />}
            </div>

            {/* Navigation Items - Terminal Style */}
            <div className="flex-1 overflow-y-auto py-3 px-3 space-y-1.5">
              {/* {showBackToChat && (
                <NavigationButton
                  icon={MessageCircle}
                  label="Return to Chat"
                  onClick={() => handleNavigation('/')}
                  highlight={true}
                  isActive={location.pathname === '/'}
                />
              )} */}

              {/* Chat History Button
              <NavigationButton
                icon={DatabaseZap}
                label="Chat History"
                onClick={() => handleNavigation('/chat-history')}
                highlight={false}
                glowColor="violet"
                isActive={location.pathname === '/chat-history'}
              /> */}

              <NavigationButton
                icon={Home}
                label={tLocal("Home")}
                onClick={() => handleNavigation('/')}
                isActive={location.pathname === '/'}
              />

              <NavigationButton
                icon={MessageCircle}
                label={tLocal("Chat")}
                onClick={() => handleNavigation('/chat')}
                isActive={location.pathname === '/chat'}
              />

              <NavigationButton
                icon={TrendingUp}
                label={tLocal("AI Exchange")}
                onClick={() => handleNavigation('/market')}
                isActive={location.pathname === '/market'}
                glowColor="violet"
              />

              {/* <NavigationButton
                icon={Users}
                label={tLocal("Community")}
                onClick={() => handleNavigation('/community')}
                isActive={location.pathname === '/community'}
              /> */}

              <NavigationButton
                icon={Settings}
                label={tLocal("Settings")}
                onClick={() => handleNavigation('/settings')}
                isActive={location.pathname === '/settings'}
              />

              {!isPro && (
                <NavigationButton
                  icon={Sparkles}
                  label={tLocal("Upgrade")}
                  onClick={() => handleNavigation('/upgrade')}
                  highlight={true}
                  isActive={location.pathname === '/upgrade'}
                />
              )}

              <NavigationButton
                icon={HelpCircle}
                label={tLocal("Help")}
                onClick={() => handleNavigation('/help')}
                isActive={location.pathname === '/help'}
              />

              {/* Added About Us navigation button */}
              <NavigationButton
                icon={Zap}
                label={tLocal("About Us")}
                onClick={() => handleNavigation('/about-us')}
                isActive={location.pathname === '/about-us'}
              />

            {/* Chat History Button */}
            <NavigationButton
                icon={DatabaseZap}
                label={tLocal("Chat Archive")}
                onClick={() => handleNavigation('/chat-history')}
                highlight={false}
                glowColor="violet"
                isActive={location.pathname === '/chat-history'}
              />

              {/* Logout Button - Terminal Style */}
              <div className="pt-2">
                <button
                  onClick={() => {
                    openLogoutModal();
                    setShowMobileMenu(false);
                  }}
                  className="w-full flex items-center gap-3 px-3 py-2.5
                    bg-red-500/[0.05] border border-red-500/20
                    rounded-lg hover:bg-red-500/[0.08] hover:border-red-500/30
                    transition-all duration-200 group"
                >
                  <LogOut className="w-4 h-4 text-red-400/60 group-hover:text-red-400/80 transition-all" />
                  <span className="text-[13px] font-mono text-red-400/70 group-hover:text-red-400/90 transition-all">
                    {tLocal("Sign Out")}
                  </span>
                </button>
              </div>
            </div>

            {/* Footer - Terminal Style */}
            <div className="p-3 border-t border-white/[0.05] bg-black/50">
              <div className="flex items-center justify-between text-white/60 text-[9px] font-mono uppercase tracking-wider">
                <span className="hover:text-white/80 transition-colors">{tLocal("Mobile Version 2.0.0")}</span>
                <span className="hover:text-white/80 transition-colors">{tLocal("© 2026 PickStar")}</span>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default MobileNavigation;